// S3/S4 — Bandeja de incidencias del supervisor (P1-P6) + resolver/descartar/
// asignar. Spec: docs/spec-mvp/roles/03-supervisor.md §8.
//
//   S3: lista con filtros (tipo, severidad, estado, línea, rango) + acciones
//       masivas (resolver varias).
//   S4: detalle completo (contexto, registro relacionado, línea, líder) +
//       acciones resolver / descartar / asignar a otro — razón obligatoria.
//
// Endpoints:
//   GET   /api/supervisor/incidencias?desde&hasta&tipo&severidad&estado&linea_id
//   GET   /api/supervisor/incidencias/:id
//   PATCH /api/supervisor/incidencias/:id/resolver    (ya existía)
//   PATCH /api/supervisor/incidencias/:id/descartar
//   PATCH /api/supervisor/incidencias/:id/asignar
//   POST  /api/supervisor/incidencias/resolver-masivo
import { z } from 'zod';
import { tipoIncidenciaSchema, severidadIncidenciaSchema } from './supervisor_dashboard.js';
export const estadoIncidenciaSchema = z.enum([
    'pendiente',
    'en_revision',
    'resuelta',
    'descartada',
]);
// =============================================================================
// Query de la bandeja (S3). Sin estado → 'activas' (pendiente + en_revision).
// =============================================================================
export const supervisorIncidenciasQuerySchema = z.object({
    /** Default backend: hoy - 7 días. */
    desde: z.string().date().optional(),
    /** Default backend: hoy. */
    hasta: z.string().date().optional(),
    tipo: tipoIncidenciaSchema.optional(),
    severidad: severidadIncidenciaSchema.optional(),
    estado: z.enum(['activas', 'pendiente', 'en_revision', 'resuelta', 'descartada']).optional(),
    linea_id: z.string().uuid().optional(),
});
// =============================================================================
// Item de la bandeja
// =============================================================================
const personaRefSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
});
export const supervisorIncidenciaItemSchema = z.object({
    id: z.string().uuid(),
    tipo: tipoIncidenciaSchema,
    severidad: severidadIncidenciaSchema,
    estado: estadoIncidenciaSchema,
    detectada_at: z.string().datetime({ offset: true }),
    /** Activas: NOW() − detectada. Cerradas: resuelta_at − detectada. */
    minutos_abierta: z.number().int(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    linea_id: z.string().uuid().nullable(),
    linea_nombre: z.string().nullable(),
    /** Descripción humana (compuesta en backend desde tipo + contexto). */
    descripcion: z.string(),
    asignada_a: personaRefSchema.nullable(),
    resuelta_at: z.string().datetime({ offset: true }).nullable(),
    resuelta_por: personaRefSchema.nullable(),
    accion_tomada: z.string().nullable(),
});
export const supervisorIncidenciasSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    items: z.array(supervisorIncidenciaItemSchema),
    /** Conteos del rango SIN el filtro de estado (para los chips del filtro). */
    conteos: z.object({
        activas: z.number().int().min(0),
        resueltas: z.number().int().min(0),
        descartadas: z.number().int().min(0),
    }),
});
// =============================================================================
// Detalle (S4): item + contexto + registro relacionado + líder + asignables
// =============================================================================
export const supervisorIncidenciaDetalleSchema = supervisorIncidenciaItemSchema.extend({
    /** contexto_json crudo de la detección (P1-P6). */
    contexto: z.record(z.string(), z.unknown()).nullable(),
    /** Registro hora×línea×SKU relacionado (null en P1/P2 sin registro). */
    registro: z
        .object({
        id: z.string().uuid(),
        fecha: z.string().date(),
        hora_inicio_bloque: z.string(),
        hora_fin_bloque: z.string(),
        sku_codigo_cliente: z.string().nullable(),
        sku_descripcion: z.string().nullable(),
        cantidad_procesada: z.number(),
        numero_operarios: z.number().int().nullable(),
        validado: z.boolean(),
    })
        .nullable(),
    /** Líder asignado del registro relacionado (o de la programación de la línea). */
    lider: personaRefSchema.nullable(),
    /** Supervisores de la planta a los que se puede asignar la incidencia. */
    asignables: z.array(personaRefSchema),
});
// =============================================================================
// Acciones S4
// =============================================================================
export const supervisorDescartarIncidenciaSchema = z.object({
    razon: z
        .string()
        .trim()
        .min(5, 'Explique por qué se descarta (mínimo 5 caracteres)')
        .max(500, 'Máximo 500 caracteres'),
});
export const supervisorAsignarIncidenciaSchema = z.object({
    asignada_a_user_id: z.string().uuid(),
});
// =============================================================================
// Acción masiva S3 — "marcar varias como resueltas"
// =============================================================================
export const supervisorResolverMasivoSchema = z.object({
    ids: z.array(z.string().uuid()).min(1, 'Seleccione al menos una').max(100),
    accion_tomada: z
        .string()
        .trim()
        .min(5, 'Describa la acción (mínimo 5 caracteres)')
        .max(500, 'Máximo 500 caracteres'),
});
export const supervisorResolverMasivoResultSchema = z.object({
    resueltas: z.number().int().min(0),
    /** Ya cerradas o fuera de scope — no se tocaron. */
    omitidas: z.number().int().min(0),
});
//# sourceMappingURL=supervisor_incidencias.js.map